import React from 'react';
import { useIntl } from 'react-intl';
import { Switch, Button, Loading } from '@gpstrackit/gtc-react-controls';
import { Tooltip, RadioGroup } from 'Components';
import HelpIcon from '@mui/icons-material/Help';
import { RadioCheck } from 'Components/RadioGroup';
import Slider from '@mui/material/Slider';
import { Line } from 'react-chartjs-2';
import { useHarshBrake } from '../../hooks/useHarshBrake';

const SLIDER_SX = { '& .MuiSlider-track': { height: 8 }, '& .MuiSlider-rail': { height: 8 } };

const SeveritySlider = ({ value, onChange, min, max, disabled, unit }) => (
    <Slider
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        disabled={disabled}
        valueLabelDisplay="auto"
        valueLabelFormat={(val) => `${val} ${unit}`}
        size="medium"
        sx={SLIDER_SX}
    />
);

const HarshBrake = ({ selectedEvent, onUnsavedChangesChange }) => {
    const { messages } = useIntl();
    const {
        formState,
        isOpenPanelEdit,
        isSaving,
        isActive,
        currentSlides,
        severityType,
        availableMethods,
        sliderMax,
        unit,
        chartData,
        chartOptions,
        disabledStyle,
        handleChangeFormState,
        handleChangeSlides,
        handleModeChange,
        handleSave,
        handleCancelEdit,
        handleSaveEdit,
        handleEditClick,
    } = useHarshBrake(selectedEvent, onUnsavedChangesChange);

    return (
        <section className="risk-events-tab-harsh-brake">
            {isSaving && <Loading overlay show />}
            <section className="risk-events-tab-harsh-brake-header">
                <p>{messages[selectedEvent?.eventName] || ''}</p>
            </section>
            <section className="risk-events-tab-harsh-brake-content">
                <div className="risk-events-tab-harsh-brake-content-switch">
                    <label htmlFor="harsh-brake-active-switch">
                        <p>{messages['driverPerformanceConfig_riskEvents_active']}</p>
                    </label>
                    <Switch
                        id="harsh-brake-active-switch"
                        labelPosition="top"
                        checked={!!formState.status}
                        size="small"
                        onChange={(value) => handleChangeFormState('status', value)}
                    />
                </div>
                <div className={`risk-events-tab-harsh-brake-config ${!isActive ? 'is-disabled' : ''}`}>
                    <div className="risk-events-tab-harsh-brake-content-switch">
                        <label htmlFor="harsh-brake-coachable-switch">
                            <p>{messages['driverPerformanceConfig_riskEvents_coachable']}</p>
                        </label>
                        <div className="risk-events-tab-harsh-brake-content-switch-icon">
                            <Switch
                                id="harsh-brake-coachable-switch"
                                labelPosition="top"
                                checked={!!formState.coachable}
                                size="small"
                                onChange={(value) => handleChangeFormState('coachable', value)}
                            />
                            <Tooltip title={messages['driverPerformanceConfig_riskEvents_coachableTooltip']}>
                                <HelpIcon/>
                            </Tooltip>
                        </div>
                    </div>

                    {severityType === 'Boolean' ? (
                        <section className="risk-events-tab-harsh-brake-content-boolean">
                            <div className="risk-events-tab-harsh-brake-content-boolean-header">
                                <p>{messages['driverPerformanceConfig_riskEvents_severityMapping']}</p>
                                <Tooltip title={messages['driverPerformanceConfig_riskEvents_severityMappingTooltip']}>
                                    <HelpIcon/>
                                </Tooltip>
                            </div>
                            <Slider
                                min={0}
                                max={100}
                                value={formState.severityValue}
                                onChange={(e, value) => handleChangeFormState('severityValue', value)}
                                disabled={!isActive}
                                valueLabelDisplay="auto"
                                track={false}
                                marks={[
                                    { value: 0, label: '' },
                                    { value: 12, label: messages['driverPerformanceConfig_riskEvents_low'] },
                                    { value: 25, label: '' },
                                    { value: 37, label: messages['driverPerformanceConfig_riskEvents_medium'] },
                                    { value: 50, label: '' },
                                    { value: 62, label: messages['driverPerformanceConfig_riskEvents_high'] },
                                    { value: 75, label: '' },
                                    { value: 87, label: messages['driverPerformanceConfig_riskEvents_critical'] },
                                    { value: 100, label: '' },
                                ]}
                            />
                        </section>
                    ) : (
                        <div className="risk-events-tab-harsh-brake-content-lineChart">
                            <div className="risk-events-tab-harsh-brake-content-lineChart-header">
                                <div className="risk-events-tab-harsh-brake-content-lineChart-header-title">
                                    <p>{messages['driverPerformanceConfig_riskEvents_severityMapping']}</p>
                                    <Tooltip title={messages['driverPerformanceConfig_riskEvents_severityMappingTooltip']}>
                                        <HelpIcon/>
                                    </Tooltip>
                                </div>
                                {isOpenPanelEdit ? (
                                    <div className="risk-events-tab-harsh-brake-content-lineChart-header-buttons">
                                        <p onClick={isActive ? handleCancelEdit : undefined} style={disabledStyle}>
                                            {messages['driverPerformanceConfig_riskEvents_cancel']}
                                        </p>
                                        <p onClick={isActive ? handleSaveEdit : undefined} style={disabledStyle}>
                                            {messages['driverPerformanceConfig_riskEvents_save']}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="risk-events-tab-harsh-brake-content-lineChart-header-button">
                                        <p onClick={isActive ? handleEditClick : undefined} style={disabledStyle}>
                                            {messages['driverPerformanceConfig_riskEvents_edit']}
                                        </p>
                                    </div>
                                )}
                            </div>
                            {isOpenPanelEdit ? (
                                <div className="risk-events-tab-harsh-brake-content-lineChart-content-edit">
                                    <div className="risk-events-tab-harsh-brake-content-lineChart-content-edit-sliders">
                                        <SeveritySlider value={currentSlides.critical} onChange={(e, value) => handleChangeSlides('critical', value)} min={0} max={sliderMax} disabled={!isActive} unit={unit} />
                                        <SeveritySlider value={currentSlides.high} onChange={(e, value) => handleChangeSlides('high', value)} min={0} max={sliderMax} disabled={!isActive} unit={unit} />
                                        <SeveritySlider value={currentSlides.medium} onChange={(e, value) => handleChangeSlides('medium', value)} min={0} max={sliderMax} disabled={!isActive} unit={unit} />
                                        <SeveritySlider value={currentSlides.low} onChange={(e, value) => handleChangeSlides('low', value)} min={0} max={sliderMax} disabled={!isActive} unit={unit} />
                                        <SeveritySlider value={currentSlides.start} onChange={(e, value) => handleChangeSlides('start', value)} min={0} max={sliderMax} disabled={!isActive} unit={unit} />
                                    </div>
                                    <div className="risk-events-tab-harsh-brake-content-lineChart-content-edit-labels">
                                        <p>{messages['driverPerformanceConfig_riskEvents_critical']}</p>
                                        <p>{messages['driverPerformanceConfig_riskEvents_high']}</p>
                                        <p>{messages['driverPerformanceConfig_riskEvents_medium']}</p>
                                        <p>{messages['driverPerformanceConfig_riskEvents_low']}</p>
                                    </div>
                                </div>
                            ) : (
                                <section className="risk-events-tab-harsh-brake-content-lineChart-content">
                                    <div className="risk-events-tab-harsh-brake-content-lineChart-content-chart">
                                        <Line data={chartData} options={chartOptions} />
                                    </div>
                                    <div className="risk-events-tab-harsh-brake-content-lineChart-content-labels">
                                    <p>{messages['driverPerformanceConfig_riskEvents_critical']}</p>
                                        <p>{messages['driverPerformanceConfig_riskEvents_high']}</p>
                                        <p>{messages['driverPerformanceConfig_riskEvents_medium']}</p>
                                        <p>{messages['driverPerformanceConfig_riskEvents_low']}</p>
                                    </div>
                                </section>
                            )}
                        </div>
                    )}

                    {severityType === 'Non-Boolean' && (
                        <section className="risk-events-tab-harsh-brake-checkbox">
                            {availableMethods.hasBoth ? (
                                <div style={disabledStyle}>
                                    <RadioGroup horizontal value={formState.radioValue} onChange={handleModeChange}>
                                        <RadioCheck label={messages['driverPerformanceConfig_riskEvents_absolute']} value="Absolute" />
                                        <RadioCheck label={messages['driverPerformanceConfig_riskEvents_relative']} value="Relative" />
                                    </RadioGroup>
                                </div>
                            ) : (
                                <p>{formState.radioValue === 'Relative' ? messages['driverPerformanceConfig_riskEvents_relative'] : messages['driverPerformanceConfig_riskEvents_absolute']}</p>
                            )}
                        </section>
                    )}

                    {!isActive && <div className="risk-events-tab-harsh-brake-config-overlay" />}
                </div>
            </section>
            <Button
                className="risk-events-tab-harsh-brake-button"
                type="button"
                isDisabled={isSaving || !selectedEvent?.profileId || !selectedEvent?.eventId}
                onClick={handleSave}
            >
                {messages['driverPerformanceConfig_riskEvents_save']}
            </Button>
        </section>
    );
};

export default HarshBrake;
